import React from 'react';
import Card from '../../components/ui/Card.jsx';
import Input from '../../components/ui/Input.jsx';
import Button from '../../components/ui/Button.jsx';
import { apiFetch } from '../../api.js';
import { useApi } from '../../hooks/useApi.js';
import { RouteContext } from '../../router.jsx';

const qualCourseFields = ['qc1', 'qc2', 'qc3', 'qc4', 'qc5'];
const prereqCourseFields = ['pc1', 'pc2', 'pc3', 'pc4', 'pc5'];
const electiveFields = ['ec1', 'ec2', 'ec3', 'ec4', 'ec5', 'ec6'];

const emptyForm = {
  cat1: '',
  cat1note: '',
  cat2: '',
  cat2note: '',
  cat31: '',
  cat32: '',
  cat3note: '',
  cat41: '',
  cat42: '',
  cat4note: '',
  ec1: '',
  ec2: '',
  ec3: '',
  ec4: '',
  ec5: '',
  ec6: '',
  ecnote: '',
  qualNeeded: false,
  prereqNeeded: false,
  wpe: false,
  comp: true,
  thesis: false,
  qc1: '',
  qc2: '',
  qc3: '',
  qc4: '',
  qc5: '',
  pc1: '',
  pc2: '',
  pc3: '',
  pc4: '',
  pc5: ''
};

export default function StaffStudentPlanEdit({ params }) {
  const { navigate } = React.useContext(RouteContext);
  const studentId = params?.id;
  const { data: me, loading: meLoading, error: meError } = useApi('/api/me');
  const { data: student, loading: studentLoading, error: studentError } = useApi(`/api/students/${studentId}`, [studentId]);
  const { data: plan, loading: planLoading, error: planError } = useApi(`/api/students/${studentId}/plan`, [studentId]);
  const [form, setForm] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState({ text: '', tone: 'info' });

  React.useEffect(() => {
    if (!plan) return;
    setForm({
      cat1: plan.cat1 || '',
      cat1note: plan.cat1note || '',
      cat2: plan.cat2 || '',
      cat2note: plan.cat2note || '',
      cat31: plan.cat31 || '',
      cat32: plan.cat32 || '',
      cat3note: plan.cat3note || '',
      cat41: plan.cat41 || '',
      cat42: plan.cat42 || '',
      cat4note: plan.cat4note || '',
      ec1: plan.ec1 || '',
      ec2: plan.ec2 || '',
      ec3: plan.ec3 || '',
      ec4: plan.ec4 || '',
      ec5: plan.ec5 || '',
      ec6: plan.ec6 || '',
      ecnote: plan.ecnote || '',
      qualNeeded: plan.qual_needed === 1,
      prereqNeeded: plan.prereq_needed === 1,
      wpe: plan.wpe === 1,
      comp: plan.comp === 1,
      thesis: plan.thesis === 1,
      qc1: plan.qc1 || '',
      qc2: plan.qc2 || '',
      qc3: plan.qc3 || '',
      qc4: plan.qc4 || '',
      qc5: plan.qc5 || '',
      pc1: plan.pc1 || '',
      pc2: plan.pc2 || '',
      pc3: plan.pc3 || '',
      pc4: plan.pc4 || '',
      pc5: plan.pc5 || ''
    });
  }, [plan]);

  React.useEffect(() => {
    if (!plan && planError && planError.includes('Plan not found')) {
      setForm(emptyForm);
    }
  }, [plan, planError]);

  if (!studentId) {
    return (
      <div className="page">
        <div className="alert error">Missing student id.</div>
      </div>
    );
  }

  const canEditPlan = !!me?.user && (me.user.chair_privilege === 1 || me.user.admin_privilege === 1 || me.user.edit_privilege === 1);

  const updateField = (key) => (event) => {
    const value = ['qualNeeded', 'prereqNeeded', 'wpe', 'comp', 'thesis'].includes(key)
      ? event.target.checked
      : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleToggle = (key, fieldsToClear) => (event) => {
    const checked = event.target.checked;
    setForm((prev) => {
      const next = { ...prev, [key]: checked };
      if (!checked) {
        fieldsToClear.forEach((field) => {
          next[field] = '';
        });
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    try {
      const payload = {
        cat1: form.cat1.trim(),
        cat1note: form.cat1note.trim(),
        cat2: form.cat2.trim(),
        cat2note: form.cat2note.trim(),
        cat31: form.cat31.trim(),
        cat32: form.cat32.trim(),
        cat3note: form.cat3note.trim(),
        cat41: form.cat41.trim(),
        cat42: form.cat42.trim(),
        cat4note: form.cat4note.trim(),
        ec1: form.ec1.trim(),
        ec2: form.ec2.trim(),
        ec3: form.ec3.trim(),
        ec4: form.ec4.trim(),
        ec5: form.ec5.trim(),
        ec6: form.ec6.trim(),
        ecnote: form.ecnote.trim(),
        qual_needed: form.qualNeeded ? 1 : 0,
        prereq_needed: form.prereqNeeded ? 1 : 0,
        wpe: form.wpe ? 1 : 0,
        comp: form.comp ? 1 : 0,
        thesis: form.thesis ? 1 : 0,
        qc1: form.qc1.trim(),
        qc2: form.qc2.trim(),
        qc3: form.qc3.trim(),
        qc4: form.qc4.trim(),
        qc5: form.qc5.trim(),
        pc1: form.pc1.trim(),
        pc2: form.pc2.trim(),
        pc3: form.pc3.trim(),
        pc4: form.pc4.trim(),
        pc5: form.pc5.trim()
      };
      await apiFetch(`/api/students/${studentId}/plan`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      setMessage({ text: 'Study plan updated.', tone: 'info' });
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to update study plan.', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const showLoading = meLoading || studentLoading || planLoading || !form;
  const fatalError = meError || studentError || (!planError ? '' : planError.includes('Plan not found') ? '' : planError);

  const studentName = student ? `${student.first_name || ''} ${student.last_name || ''}`.trim() : '';

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Edit Study Plan</h1>
          <p className="subtle">Update course categories, electives, and completion requirements.</p>
        </div>
        <div className="header-actions">
          <Button variant="secondary" type="button" onClick={() => navigate(`/staff/students/${studentId}`)}>
            Back to Profile
          </Button>
        </div>
      </div>

      {fatalError ? <div className="alert error">{fatalError}</div> : null}
      {message.text ? <div className={message.tone === 'error' ? 'alert error' : 'alert'}>{message.text}</div> : null}

      {showLoading ? (
        <p className="subtle">Loading...</p>
      ) : !canEditPlan ? (
        <div className="alert error">You do not have access to edit this study plan.</div>
      ) : (
        <>
          <Card title="Student">
            <div className="summary-list">
              <div className="summary-row">
                <span>Name</span>
                <span>{studentName || '—'}</span>
              </div>
              <div className="summary-row">
                <span>CIN</span>
                <span>{student?.cin || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Campus Email</span>
                <span>{student?.username || '—'}</span>
              </div>
            </div>
          </Card>

          <Card title="Core Categories">
            <div className="form-grid">
              <Input label="Category 1 Course" value={form.cat1} onChange={updateField('cat1')} placeholder="EE 5000" />
              <label className="input-field">
                <span className="input-label">Category 1 Notes</span>
                <textarea className="input" rows="3" value={form.cat1note} onChange={updateField('cat1note')} />
              </label>
              <Input label="Category 2 Course" value={form.cat2} onChange={updateField('cat2')} placeholder="EE 5100" />
              <label className="input-field">
                <span className="input-label">Category 2 Notes</span>
                <textarea className="input" rows="3" value={form.cat2note} onChange={updateField('cat2note')} />
              </label>
              <Input label="Category 3 Course 1" value={form.cat31} onChange={updateField('cat31')} placeholder="EE 5200" />
              <Input label="Category 3 Course 2" value={form.cat32} onChange={updateField('cat32')} placeholder="EE 5210" />
              <label className="input-field">
                <span className="input-label">Category 3 Notes</span>
                <textarea className="input" rows="3" value={form.cat3note} onChange={updateField('cat3note')} />
              </label>
              <Input label="Category 4 Course 1" value={form.cat41} onChange={updateField('cat41')} placeholder="EE 5300" />
              <Input label="Category 4 Course 2" value={form.cat42} onChange={updateField('cat42')} placeholder="EE 5310" />
              <label className="input-field">
                <span className="input-label">Category 4 Notes</span>
                <textarea className="input" rows="3" value={form.cat4note} onChange={updateField('cat4note')} />
              </label>
            </div>
          </Card>

          <Card title="Electives">
            <div className="form-grid">
              {electiveFields.map((field, index) => (
                <Input
                  key={field}
                  label={`Elective ${index + 1}`}
                  value={form[field]}
                  onChange={updateField(field)}
                  placeholder="EE 5xxx"
                />
              ))}
              <label className="input-field">
                <span className="input-label">Elective Notes</span>
                <textarea className="input" rows="3" value={form.ecnote} onChange={updateField('ecnote')} />
              </label>
            </div>
          </Card>

          <Card title="Requirements">
            <div className="toggle-row">
              <div>
                <div className="subtle">Enable if the student has to take qualification courses.</div>
              </div>
              <label className="switch">
                <input type="checkbox" checked={form.qualNeeded} onChange={handleToggle('qualNeeded', qualCourseFields)} />
                <span className="slider" />
              </label>
            </div>
            {form.qualNeeded ? (
              <div className="form-grid">
                {qualCourseFields.map((field, index) => (
                  <Input
                    key={field}
                    label={`Qualification Course ${index + 1}`}
                    value={form[field]}
                    onChange={updateField(field)}
                    placeholder="EE 4xxx"
                  />
                ))}
              </div>
            ) : null}

            <div className="toggle-row mt-3">
              <div>
                <div className="subtle">Enable if the student has to take prerequisite courses.</div>
              </div>
              <label className="switch">
                <input type="checkbox" checked={form.prereqNeeded} onChange={handleToggle('prereqNeeded', prereqCourseFields)} />
                <span className="slider" />
              </label>
            </div>
            {form.prereqNeeded ? (
              <div className="form-grid">
                {prereqCourseFields.map((field, index) => (
                  <Input
                    key={field}
                    label={`Prerequisite Course ${index + 1}`}
                    value={form[field]}
                    onChange={updateField(field)}
                    placeholder="EE 4xxx"
                  />
                ))}
              </div>
            ) : null}

            <div className="toggle-row mt-3">
              <div>
                <div className="subtle">Mark if the Writing Proficiency Exam is required.</div>
              </div>
              <label className="switch">
                <input type="checkbox" checked={form.wpe} onChange={updateField('wpe')} />
                <span className="slider" />
              </label>
            </div>
          </Card>

          <Card title="Completion Options">
            <div className="toggle-row">
              <div>
                <div className="label">Comprehensive Exam</div>
                <div className="subtle">Student plans to complete the comprehensive exam.</div>
              </div>
              <label className="switch">
                <input type="checkbox" checked={form.comp} onChange={updateField('comp')} />
                <span className="slider" />
              </label>
            </div>
            <div className="toggle-row">
              <div>
                <div className="label">Thesis</div>
                <div className="subtle">Student plans to complete a thesis.</div>
              </div>
              <label className="switch">
                <input type="checkbox" checked={form.thesis} onChange={updateField('thesis')} />
                <span className="slider" />
              </label>
            </div>
          </Card>

          <div className="form-actions">
            <Button variant="secondary" type="button" onClick={() => navigate(`/staff/students/${studentId}`)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving || !canEditPlan}>
              {saving ? 'Saving...' : 'Save Study Plan'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
